// TES-1289 — the admin side of "Sammanfattning efter vårdmötet": which
// meetings offer it, the prefill from the choices the doctor has made in the
// guided steps (not saved yet), what the field shows, and why it may not be
// published. The prefill and its rules are the contract's
// (meeting-summary.ts), so the admin and the API compute the same text.
//
// Pure on purpose: no React, no path aliases. The unit tests in
// `apps/admin/test` run it with plain `node --test`.
import {
  buildMeetingSummaryPrefill,
  DOCTOR_NOTICE_CLAIM_ERROR,
  finalizeMeetingSummary,
  MEETING_SUMMARY_STATUS_LABEL,
  meetingSummaryUnconfirmedClaims,
  meetingSummaryUpdate,
  type CompletionItem,
  type CompletionOutcome,
  type MeetingSummaryView,
} from "@care-by-ella/contracts";

/**
 * The summary belongs to the meeting's outcome "Godkänd för behandling"
 * (stored as `proceed`) and "Komplettering behövs". Not "Kan inte gå vidare",
 * whose text is the patient notice alone (Ö-529, answered 2026-10-15), and
 * never a voluntary meeting.
 */
export function meetingShowsSummary(
  outcome: CompletionOutcome | null,
  voluntary: boolean,
): boolean {
  return !voluntary && (outcome === "proceed" || outcome === "completion");
}

/** The prefill from the guided steps' current choices. */
export function meetingSummaryPrefillFrom(input: {
  outcome: CompletionOutcome | null;
  approved: boolean;
  items: readonly CompletionItem[];
  otherHeading?: string;
  order: { medication: string; dose: string; frequency: string };
  labOrderConfirmed: boolean;
}): string {
  const medication = input.order.medication.trim();
  return buildMeetingSummaryPrefill({
    outcome: input.outcome,
    approved: input.outcome === "proceed" && input.approved,
    items: input.outcome === "completion" ? input.items : [],
    otherHeading: input.otherHeading ?? null,
    plannedMedication: medication
      ? {
          medication,
          dose: input.order.dose.trim() || null,
          frequency: input.order.frequency.trim() || null,
        }
      : null,
    labOrderConfirmed: input.labOrderConfirmed,
  });
}

// The doctor's text as it stands in this browser and has been sent (or is
// about to be sent) to the server.
export interface MeetingSummaryLocal {
  body: string;
  basisPrefill: string;
}

export interface MeetingSummaryShown {
  text: string;
  edited: boolean;
  // The new prefill to offer when the sources changed after her edit.
  proposal: string | null;
  // The prefill her text is based on, sent with every save.
  basisPrefill: string;
  status: string;
  // The published version is the current text: publishing again sends
  // nothing (MeetingSummaryService.publish).
  unchangedSincePublish: boolean;
}

/** What the field shows, from the server's draft and her local text. */
export function meetingSummaryShown(
  server: MeetingSummaryView | null,
  local: MeetingSummaryLocal | null,
  prefill: string,
): MeetingSummaryShown {
  const draft = local
    ? {
        body: local.body,
        basisPrefill: local.basisPrefill,
        editedByDoctorAt: local.body !== local.basisPrefill ? "local" : null,
      }
    : (server?.draft ?? null);
  const update = meetingSummaryUpdate(draft, prefill);
  const published = server?.draft?.published ?? null;
  const unchangedSincePublish =
    server?.draft?.status === "published" &&
    finalizeMeetingSummary(update.text) ===
      finalizeMeetingSummary(server.draft.body);
  const status =
    published && unchangedSincePublish && update.proposal === null
      ? `${MEETING_SUMMARY_STATUS_LABEL.published} · version ${published.version}`
      : update.edited
        ? MEETING_SUMMARY_STATUS_LABEL.edited
        : MEETING_SUMMARY_STATUS_LABEL.prefilled;
  return {
    ...update,
    unchangedSincePublish: Boolean(published && unchangedSincePublish),
    basisPrefill: update.edited ? draft!.basisPrefill : prefill,
    status,
  };
}

/** Her text after she types: based on what she was looking at. */
export function meetingSummaryAfterEdit(
  shown: MeetingSummaryShown,
  text: string,
): MeetingSummaryLocal {
  return { body: text, basisPrefill: shown.basisPrefill };
}

/** "Uppdatera utkastet": the proposal becomes the text and its basis. */
export function meetingSummaryApplied(proposal: string): MeetingSummaryLocal {
  return { body: proposal, basisPrefill: proposal };
}

/** "Behåll min text": her text, now based on the new prefill. */
export function meetingSummaryKept(
  shown: MeetingSummaryShown,
): MeetingSummaryLocal {
  return {
    body: shown.text,
    basisPrefill: shown.proposal ?? shown.basisPrefill,
  };
}

/** Why the summary may not be published yet, in the doctor's words. */
export function meetingSummaryClaimErrors(
  text: string,
  labOrderConfirmed: boolean,
): string[] {
  return meetingSummaryUnconfirmedClaims(text, labOrderConfirmed).map(
    (claim) => DOCTOR_NOTICE_CLAIM_ERROR[claim],
  );
}
